import { column, Css, emptyCell, GridColumn, GridDataRow, GridTable, simpleHeader, Tag } from "@homebound/beam";
import { Link } from "react-router-dom";
import { dateCell, Price } from "src/components";
import { billStatusTagMapper } from "src/components/detailPane/bill/utils";
import {
  BudgetSuperDrawer_BillFragment,
  BudgetSuperDrawer_BillLineItemFragment,
  BudgetSuperDrawer_ExpenseAllocationFragment,
  BudgetSuperDrawer_ExpenseFragment,
  BudgetSuperDrawer_ProjectItemFragment,
} from "src/routes/projects/budget/components/BudgetSuperDrawer.generated";
import { createProjectExpensesUrl } from "src/RouteUrls";
import { sum } from "src/utils";

export type BudgetBillsTableProps = { projectId: string; projectItems: BudgetSuperDrawer_ProjectItemFragment[] };

/** Bills table displays all actuals (bills and reallocated direct expenses) */
export function BudgetBillsTable(props: BudgetBillsTableProps) {
  const { projectItems, projectId } = props;
  const rows = createRows(projectItems);
  const columns = createColumns(projectId, projectItems);

  return (
    <div>
      <div css={Css.smSb.h3.mb2.$} data-testid="title">
        Trade Paid
      </div>
      <GridTable id="bills" columns={columns} rows={rows} />
    </div>
  );
}

function createRows(projectItems: BudgetSuperDrawer_ProjectItemFragment[]): GridDataRow<Row>[] {
  const billLineItems = projectItems.flatMap((pi) => pi.billLineItems);
  const expensesAllocations = projectItems.flatMap((pi) => pi.expenseAllocations);

  const perItemsBilled = calcPerItem(billLineItems, expensesAllocations, "billed");
  const perItemsPaid = calcPerItem(billLineItems, expensesAllocations, "paid");

  const bills = billLineItems.map((bli) => bli.bill).unique();
  const expenses = expensesAllocations.map((ea) => ea.expense).unique();
  return [
    simpleHeader,
    ...bills.map((b) => ({ kind: "bill" as const, id: b.id, data: b })),
    ...expenses.map((e) => ({ kind: "expense" as const, id: e.id, data: e })),
    {
      kind: "totals" as const,
      id: "total",
      data: {
        billed: sum(
          bills.sum((b) => (b.isTradePartnerCredit ? -b.billedInCents : b.billedInCents)),
          expenses.sum((e) => e.amountInCents),
        ),
        perItemsBilled,
        perItemsPaid,
        perItemsBalance: perItemsBilled - perItemsPaid,
      },
    },
  ];
}

function createColumns(projectId: string, projectItems: BudgetSuperDrawer_ProjectItemFragment[]): GridColumn<Row>[] {
  const billLineItems = projectItems.flatMap((pi) => pi.billLineItems);
  const expensesAllocations = projectItems.flatMap((pi) => pi.expenseAllocations);

  return [
    column<Row>({
      header: "Bill",
      // Link to Bill by id, or to all expenses in the project
      bill: (data) => <Link to={data.blueprintUrl.path}>{data.tradePartnerNumber}</Link>,
      expense: () => <Link to={createProjectExpensesUrl(projectId)}>Expense</Link>,
      totals: "Total",
      w: "100px",
    }),
    column<Row>({
      header: "Status",
      bill: (data) => {
        const [billType, text] = billStatusTagMapper(data);
        return <Tag type={billType} text={text} />;
      },
      expense: "Paid",
      totals: emptyCell,
      w: "120px",
    }),
    column<Row>({
      header: "Trade",
      bill: (data) => data.tradePartner.name,
      expense: (data) => `${data.intacctVendorName} - ${data.description}`,
      totals: emptyCell,
      w: "190px",
    }),
    column<Row>({
      header: "Due Date",
      bill: (data) => dateCell(data.dueDate),
      expense: (data) => dateCell(data.expenseDate),
      totals: emptyCell,
      w: "100px",
    }),
    column<Row>({
      header: "Billed",
      bill: (b) => <Price valueInCents={b.isTradePartnerCredit ? -b.billedInCents : b.billedInCents} />,
      expense: (e) => <Price valueInCents={e.amountInCents} />,
      totals: (t) => <Price valueInCents={t.billed} />,
      w: "100px",
    }),
    column<Row>({
      header: "Per Item Billed",
      bill: (b) => <Price valueInCents={calcPerItem(billLineItems, [], "billed", b.id)} />,
      expense: (e) => <Price valueInCents={calcPerItem([], expensesAllocations, "billed", e.id)} />,
      totals: (t) => <Price valueInCents={t.perItemsBilled} />,
      w: "100px",
    }),
    column<Row>({
      header: "Per Item Paid",
      bill: (b) => <Price valueInCents={calcPerItem(billLineItems, [], "paid", b.id)} />,
      expense: (e) => <Price valueInCents={calcPerItem([], expensesAllocations, "paid", e.id)} />,
      totals: (t) => <Price valueInCents={t.perItemsPaid} />,
      w: "120px",
    }),
    column<Row>({
      header: "Per Item Balance",
      bill: (b) => {
        const billed = calcPerItem(billLineItems, [], "billed", b.id);
        const paid = calcPerItem(billLineItems, [], "paid", b.id);
        return <Price valueInCents={billed - paid} />;
      },
      expense: (e) => {
        const billed = calcPerItem([], expensesAllocations, "billed", e.id);
        const paid = calcPerItem([], expensesAllocations, "paid", e.id);
        return <Price valueInCents={billed - paid} />;
      },
      totals: (t) => <Price valueInCents={t.perItemsBalance} />,
      w: "390px",
    }),
  ];
}
type Row = { kind: "header" } | BillsRow | ExpenseRow | TotalRow;
type BillsRow = { kind: "bill"; id: string; data: BudgetSuperDrawer_BillFragment };

type ExpenseRow = { kind: "expense"; id: string; data: BudgetSuperDrawer_ExpenseFragment };

type TotalRow = {
  kind: "totals";
  data: { billed: number; perItemsBilled: number; perItemsPaid: number; perItemsBalance: number };
};

function calcPerItem(
  billLineItems: BudgetSuperDrawer_BillLineItemFragment[],
  expensesAllocations: BudgetSuperDrawer_ExpenseAllocationFragment[],
  stat: "billed" | "paid",
  forBillOrExpenseId?: string,
): number {
  return sum(
    billLineItems
      .filter((bli) => (forBillOrExpenseId ? bli.bill.id === forBillOrExpenseId : true))
      .sum((bli) => {
        const amount = stat === "billed" ? bli.amountInCents : bli.paidInCents;
        return bli.bill.isTradePartnerCredit ? -amount : amount;
      }),
    expensesAllocations
      .filter((ea) => (forBillOrExpenseId ? ea.expense.id === forBillOrExpenseId : true))
      .sum((ea) => (stat === "billed" ? ea.amountInCents : ea.paidAmountInCents)),
  );
}
